import { z } from "zod";

const costTiersSchema = z.object({
  base: z.number().min(0).optional(),
  plus: z.number().min(0).optional(),
  premium: z.number().min(0).optional(),
});

export const sizeOptionSchema = z.object({
  size: z.string().min(1),
  cost: z.number().min(0),
  retailPrice: z.number().min(0).optional(),
  costTiers: costTiersSchema.optional(),
  shippingCost: z.number().min(0).optional(),
  shippingTiers: costTiersSchema.optional(),
  stock: z.number().min(0).optional(),
  supplierVariantId: z.string().optional(),
  attributes: z.record(z.string()).optional(),
  byCountry: z.record(z.object({
    costTiers: costTiersSchema.optional(),
    shippingTiers: costTiersSchema.optional(),
    printCosts: z.record(costTiersSchema).optional(),
  })).optional(),
  printCosts: z.record(costTiersSchema).optional(),
});

export const colorAvailabilitySchema = z.object({
  color: z.string().min(1),
  sizes: z.record(z.string(), z.boolean()),
});

export const categorySourceInfoSchema = z.object({
  supplier: z.string().optional(),
  productId: z.string().optional(),
  sku: z.string().optional(),
  url: z.string().optional(),
  descriptionHtml: z.string().optional(),
  images: z.array(z.string()).optional(),
  printTypes: z.array(z.string()).optional(),
  printType: z.string().optional(),
  printSides: z.array(z.string()).optional(),
  country: z.string().optional(),
  currency: z.string().optional(),
  productionLocation: z.string().optional(),
  productionDuration: z.string().optional(),
  productionPartner: z.string().optional(),
  estimatedDeliveryTime: z.string().optional(),
  trEligible: z.boolean().optional(),
  // Bu urun hangi pazarlar icin uygun: ["US"], ["TR"] veya ikisi birden.
  // Bos ise pazar bilgisi girilmemis demektir (filtrede "belirsiz").
  markets: z.array(z.enum(["US", "TR"])).optional(),
  fetchedAt: z.string().optional(),
});

export const productCategorySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  brand: z.string().min(1),
  sizeOptions: z.array(sizeOptionSchema).min(1),
  colorAvailability: z.array(colorAvailabilitySchema),
  source: categorySourceInfoSchema.optional(),
  group: z.string().optional(),
});

export const updateCatalogGroupsSchema = z.object({
  assignments: z.array(z.object({
    categoryId: z.string().min(1),
    group: z.string().nullable(),
  })).optional(),
  addGroup: z.string().min(1).max(60).optional(),
  removeGroup: z.string().min(1).optional(),
  renameGroup: z.object({ from: z.string().min(1), to: z.string().min(1).max(60) }).optional(),
});

// ---- Rexven içe aktarma ----

export const rexvenVariantSchema = z.object({
  // Bedensiz urunler (taki/aksesuar) icin opsiyonel — sunucu tarafinda tek bir
  // "Standart" secenege donusturulur.
  size: z.string().min(1).optional(),
  attributes: z.record(z.string()).optional(),
  country: z.string().max(8).optional(),
  color: z.string().optional(),
  printType: z.string().optional(),
  printCosts: z.record(costTiersSchema).optional(),
  cost: z.number().min(0).optional(),
  costPlus: z.number().min(0).optional(),
  costPremium: z.number().min(0).optional(),
  shipping: z.number().min(0).optional(),
  shippingPlus: z.number().min(0).optional(),
  shippingPremium: z.number().min(0).optional(),
  stock: z.number().min(0).optional(),
  variantId: z.union([z.string(), z.number()]).optional(),
});

export const rexvenProductSchema = z.object({
  id: z.union([z.string(), z.number()]),
  title: z.string().min(1),
  sku: z.string().optional(),
  brand: z.string().optional(),
  description: z.string().optional(),
  images: z.array(z.string()).optional(),
  url: z.string().optional(),
  sizes: z.array(z.string()).optional(),
  colors: z.array(z.string()).optional(),
  printTypes: z.array(z.string()).optional(),
  printType: z.string().optional(),
  printSides: z.array(z.string()).max(6).optional(),
  country: z.string().optional(),
  currency: z.string().optional(),
  productionLocation: z.string().optional(),
  productionDuration: z.string().optional(),
  productionPartner: z.string().optional(),
  estimatedDeliveryTime: z.string().optional(),
  trEligible: z.boolean().optional(),
  markets: z.array(z.enum(["US", "TR"])).optional(),
  variationTypes: z.array(z.object({ name: z.string(), values: z.array(z.string()) })).optional(),
  allAxes: z.array(z.object({ name: z.string(), values: z.array(z.string()) })).max(30).optional(),
  attributes: z.record(z.string()).optional(),
  variants: z.array(rexvenVariantSchema).min(1),
  /** Eklentide olculen eksen rolleri (fiyat yalnizca GERCEKTEN fiyati degisen eksen) */
  axisRoles: z.record(z.enum(["price", "variation", "personalization", "fixed"])).optional(),
  /** Etsy'de eksen basligi ("Number of Circles") ve deger etiketleri
   *  ("1 Halka-1 Birthstone" -> "1 Circle + 1 Stone") */
  axisTitles: z.record(z.string().max(45)).optional(),
  axisValueLabels: z.record(z.record(z.string().max(60))).optional(),
  /** Urunun grubu (Kolye, Bileklik…) — kategori sayfasinda urun basina */
  group: z.string().max(60).optional(),
  /** Parca olculeri (mm) — aciklamadan okunur, eklentide duzenlenir */
  physicalSize: z.object({ widthMm: z.number().positive().max(500).optional(), heightMm: z.number().positive().max(500).optional() }).optional(),
  /** Eklentide secilen secenek setleri (Secenek Kutuphanesi) */
  optionRefs: z.array(z.object({
    optionSetId: z.string().min(1),
    role: z.enum(["variation", "personalization"]),
    required: z.boolean().optional(),
    values: z.array(z.string()).optional(),
    chartOnly: z.boolean().optional(),
  })).max(12).optional(),
  /** Eklentide yapilan AI liste analizi (sunucuda yeniden temizlenir) */
  listingAnalysis: z.unknown().optional(),
});

export const importRexvenSchema = z.object({
  catalogId: z.string().optional(),
  name: z.string().min(1).max(200).optional(),
  supplier: z.string().optional(),
  priceTier: z.enum(["base", "plus", "premium"]).optional(),
  // true: SKU zaten katalogda olsa bile uzerine yaz
  overwrite: z.boolean().optional().default(false),
  group: z.string().max(60).optional(),
  /** Yeni katalog acilirken dikeyi (taki urunleri tekstil katalogunda durmasin) */
  vertical: z.enum(["apparel", "giftware", "jewelry"]).optional(),
  /** true: aktarilan urunler icin arka planda AI liste analizi baslatilir */
  analyze: z.boolean().optional(),
  /** Arka plan analizinin modeli (eklentide secilir) */
  analyzeModel: z.string().max(60).optional(),
  products: z.array(rexvenProductSchema).min(1),
});

export type ImportRexvenInput = z.infer<typeof importRexvenSchema>;

export const createSupplierCatalogSchema = z.object({
  name: z.string().min(1).max(200),
  vertical: z.enum(["apparel", "giftware", "jewelry"]).optional(),
  supplier: z.string().optional(),
  description: z.string().optional(),
  categories: z.array(productCategorySchema),
  metadata: z.object({
    importSource: z.string().optional(),
    originalFileName: z.string().optional(),
    importedAt: z.string().optional(),
    notes: z.string().optional(),
  }).optional(),
});

export const updateSupplierCatalogSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  supplier: z.string().optional(),
  description: z.string().optional(),
  categories: z.array(productCategorySchema).optional(),
  metadata: z.object({
    importSource: z.string().optional(),
    originalFileName: z.string().optional(),
    importedAt: z.string().optional(),
    notes: z.string().optional(),
  }).optional(),
});

export const pricingStrategySchema = z.object({
  mode: z.enum(["margin_percent", "fixed_profit", "target_price", "catalog_fixed"]),
  marginPercent: z.number().min(0).max(99).optional(),
  fixedProfit: z.number().min(0).optional(),
  targetBasePrice: z.number().min(0).optional(),
  etsyFeePercent: z.number().min(0).default(6.5),
  etsyListingFee: z.number().min(0).default(0.20),
  etsyTransactionFee: z.number().min(0).default(0),
  shippingCost: z.number().min(0).default(0),
  otherCosts: z.number().min(0).default(0),
  shippingCharge: z.number().min(0).optional(),
  offsiteAds: z.boolean().optional(),
  discountPct: z.number().min(0).max(90).optional(),
});

export const generateVariationsSchema = z.object({
  catalogId: z.string().min(1),
  categoryId: z.string().min(1),
  selectedColors: z.array(z.string()).optional(),
  selectedSizes: z.array(z.string()).optional(),
  sizePrefix: z.string().optional().default(""),
  pricing: pricingStrategySchema,
  onlyInStock: z.boolean().optional().default(true),
});

export type CreateSupplierCatalogInput = z.infer<typeof createSupplierCatalogSchema>;
export type UpdateSupplierCatalogInput = z.infer<typeof updateSupplierCatalogSchema>;
// Istek govdesi: varsayilanli alanlar (eski Etsy ucret alanlari dahil) gonderilmeyebilir
export type GenerateVariationsInput = z.input<typeof generateVariationsSchema>;
